import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import API_BASE from "../config/api";

/* ================= HELPERS ================= */
const normalize = (s = "") =>
  String(s)
    .replace(/\u2013|\u2014/g, "-")
    .replace(/[()\/.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const TradePage = () => {
  const { metalKey, action } = useParams();
  const navigate = useNavigate();

  const safeMetalKey = metalKey ? decodeURIComponent(metalKey) : "";
  const safeAction = action?.toLowerCase() || "";

  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const [rate, setRate] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [total, setTotal] = useState(0);
  const [rateChanged, setRateChanged] = useState(false);

  const prevRateRef = useRef(null);

  const [isTradingAllowed, setIsTradingAllowed] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderLocked, setOrderLocked] = useState(false);
  const [orderMessage, setOrderMessage] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);

  const isSilver = safeMetalKey.toLowerCase().includes("silver");
  const silverDropdownItems = [1, 5, 10, 15, 20, 25, 30, 40, 50];

  /* ================= AUTH GUARD ================= */
  useEffect(() => {
    if (!user || !token) navigate("/login");
  }, [user, token, navigate]);

  /* ================= RATE LOAD ================= */
  useEffect(() => {
    if (!safeMetalKey || !safeAction) return;

    const readCachedRates = () => {
      try {
        const cached = localStorage.getItem("cached_custom_rates");
        return cached ? JSON.parse(cached) : null;
      } catch (error) {
        console.error("Failed to parse cached custom rates:", error);
        return null;
      }
    };

    const applyRates = (customRates) => {
      if (!Array.isArray(customRates)) return;

      const metal =
        customRates.find((r) => r.key === safeMetalKey) ||
        customRates.find(
          (r) => normalize(r.name) === normalize(safeMetalKey)
        );

      if (!metal) return;

      setDisplayName(metal.name);

      const pickedRate =
        safeAction === "buy" ? metal.buy : metal.sell;

      if (pickedRate == null) return;

      if (pickedRate !== prevRateRef.current) {
        prevRateRef.current = pickedRate;
        setRate(pickedRate);
        setRateChanged(true);
        setTimeout(() => setRateChanged(false), 300);
      } else {
        setRate(pickedRate);
      }
    };

    let didLoadLiveRates = false;

    const fetchRates = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/rates`, {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        const customRates = res.data?.custom || [];

        localStorage.setItem(
          "cached_custom_rates",
          JSON.stringify(customRates)
        );

        applyRates(customRates);
        didLoadLiveRates = true;
      } catch {
        if (!didLoadLiveRates) {
          const cachedRates = readCachedRates();
          if (cachedRates) {
            applyRates(cachedRates);
          }
        }
      }
    };

    fetchRates();
    const interval = setInterval(fetchRates, 3000);
    return () => clearInterval(interval);
  }, [safeMetalKey, safeAction]);

  /* ================= TOTAL CALC ================= */
  useEffect(() => {
    if (rate == null || !quantity) return;
    const adjustedRate = isSilver ? rate : rate / 10;
    setTotal(quantity * adjustedRate);
  }, [rate, quantity, isSilver]);

  /* ================= TRADING HOURS ================= */
  useEffect(() => {
    const checkTradingHours = () => {
      try {
        const nowIST = new Date().toLocaleString("en-US", {
          timeZone: "Asia/Kolkata",
        });
        const istDate = new Date(nowIST);
        const d = istDate.getDay();
        const h = istDate.getHours();
        const m = istDate.getMinutes();
        setIsTradingAllowed(
          !(d === 0 || h < 12 || h > 18 || (h === 18 && m > 30))
        );
      } catch {
        const now = new Date();
        const d = now.getDay();
        const h = now.getHours();
        const m = now.getMinutes();
        setIsTradingAllowed(
          !(d === 0 || h < 12 || h > 18 || (h === 18 && m > 30))
        );
      }
    };
    checkTradingHours();
    const t = setInterval(checkTradingHours, 60000);
    return () => clearInterval(t);
  }, []);

  /* ================= PLACE ORDER ================= */
  const handleOrder = async () => {
    if (orderLocked) return;

    if (!quantity || quantity <= 0) {
      setOrderMessage("Invalid quantity");
      return;
    }

    if (rate == null) {
      setOrderMessage("Rate unavailable");
      return;
    }

    if (!isTradingAllowed) {
      setOrderMessage("⛔ Trading closed (Trading allowed Mon-Sat 12:00 PM – 6:30 PM IST)");
      return;
    }

    try {
      setPlacingOrder(true);
      setOrderMessage("");

      const res = await fetch(`${API_BASE}/api/trades`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          metal: displayName || safeMetalKey,
          rate,
          quantity,
          action: safeAction,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const createdTradeId = data.tradeId || data.trade?._id;
        setOrderSuccess(true);
        setOrderMessage("🔒 Price locked! Redirecting to Payment Gateway...");
        setOrderLocked(true);
        setTimeout(() => {
          navigate(`/payment/${createdTradeId}`);
        }, 1200);
      } else {
        const data = await res.json().catch(() => null);
        setOrderMessage(data?.message ? `❌ ${data.message}` : "❌ Failed to place order");
      }
    } catch {
      setOrderMessage("❌ Server down");
    } finally {
      setPlacingOrder(false);
    }
  };

  return (
    <PageWrapper>
      <Card>
        <BackButton onClick={() => navigate("/")}>← Back</BackButton>

        <Title>
          {safeAction.toUpperCase()} — {displayName || safeMetalKey}
        </Title>

        {rate != null ? (
          <RateInfo rateChanged={rateChanged}>
            Current {safeAction} rate: <strong>₹ {rate}</strong>
          </RateInfo>
        ) : (
          <RateInfo>Rate loading…</RateInfo>
        )}

        {isSilver ? (
          <>
            <Label>Select Quantity (kg)</Label>
            <Select
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              disabled={orderLocked}
            >
              {silverDropdownItems.map((q) => (
                <option key={q} value={q}>
                  {q} kg
                </option>
              ))}
            </Select>
          </>
        ) : (
          <>
            <Label>Enter Quantity (grams)</Label>
            <Input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              disabled={orderLocked}
            />
          </>
        )}

        <Total>
          Total:{" "}
          <strong>
            ₹ {rate != null ? total.toLocaleString() : "-"}
          </strong>
        </Total>

        {orderMessage && (
          <Message success={orderSuccess}>{orderMessage}</Message>
        )}

        <Button
  onClick={handleOrder}
  disabled={!isTradingAllowed || placingOrder || orderLocked}
>
  {orderLocked
    ? "🔒 Price Locked! Opening Payment..."
    : placingOrder
    ? "⏳ Locking Price..."
    : !isTradingAllowed
    ? "⛔ Trading Closed (Mon-Sat 12–6:30 PM)"
    : safeAction === "buy"
    ? "🔒 Lock Price & Pay"
    : "🔒 Lock Price & Book"}
</Button>

      </Card>
    </PageWrapper>
  );
};

export default TradePage;


/* ================= STYLES (UNCHANGED) ================= */

const PageWrapper = styled.div`
  background: #000;
  color: #f5d76e;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: #111;
  padding: 2rem;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  position: relative;
`;

const Title = styled.h2`
  text-align: center;
`;

const RateInfo = styled.p`
  text-align: center;
  margin: 1rem 0;
  background: ${(p) => (p.rateChanged ? "#f5d76e" : "transparent")};
  color: ${(p) => (p.rateChanged ? "#000" : "#f5d76e")};
  padding: 0.4rem;
  border-radius: 6px;
`;

const Label = styled.label`
  margin-top: 0.8rem;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem;
  background: #1a1a1a;
  border: 1px solid #f5d76e;
  color: #f5d76e;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.7rem;
  background: #1a1a1a;
  border: 1px solid #f5d76e;
  color: #f5d76e;
`;

const Total = styled.p`
  text-align: center;
  margin: 1rem 0;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.8rem;
  background: #f5d76e;
  color: #000;
  font-weight: bold;
  border-radius: 8px;
`;

const BackButton = styled.button`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: transparent;
  border: 1px solid #f5d76e;
  color: #f5d76e;
`;

const Message = styled.div`
  text-align: center;
  margin: 1rem 0;
  color: ${(p) => (p.success ? "#00ff99" : "#ff4d4d")};
`;
